import { useEffect } from "react";
import { Workbox } from "workbox-window";
import { toast } from "~served/ui-comps/toast";
import { useWindowFocus } from "~served/ui-comps/hooks/use-window-focus";
import { queryClient } from "~/providers/query-provider";
import { setState } from "~/store";

declare global {
	interface Window {
		workbox?: Workbox;
	}
}

const showUpdateInstalledToast = () =>
	toast.message("Update is installed", {
		id: "installed-toast",
		action: {
			label: "Reload",
			onClick: () => {
				queryClient.clear();
				window.location.reload();
			},
		},
	});

export const AppUpdateChecker = () => {
	const isWindowFocus = useWindowFocus();

	useEffect(() => {
		if (
			isWindowFocus &&
			"serviceWorker" in navigator &&
			window.workbox !== undefined
		) {
			void navigator.serviceWorker.ready.then((registration) => {
				void registration.update();
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isWindowFocus]);

	// https://developer.chrome.com/docs/workbox/modules/workbox-window#type-WorkboxLifecycleEventMap
	useEffect(() => {
		const installingToastId = "installing-toast";

		if ("serviceWorker" in navigator && window.workbox !== undefined) {
			const { workbox: wb } = window;

			const handleLog = console.log;

			wb.addEventListener("installing", handleLog);
			wb.addEventListener("message", handleLog);
			wb.addEventListener("installed", handleLog);
			wb.addEventListener("redundant", handleLog);
			wb.addEventListener("activating", handleLog);
			wb.addEventListener("controlling", handleLog);
			wb.addEventListener("waiting", (e) => {
				handleLog(e);
				wb.messageSkipWaiting();
				toast.dismiss(installingToastId);
				showUpdateInstalledToast();
				setState({ isUpdateInstalled: false });
			});
			wb.addEventListener("activated", (e) => {
				handleLog(e);
				toast.dismiss(installingToastId);
				if (e.isUpdate) {
					showUpdateInstalledToast();
					setState({ isUpdateInstalled: false });
				}
			});

			void navigator.serviceWorker.ready.then((registration) => {
				registration.addEventListener("updatefound", (...args) => {
					toast.loading("Installing updates...", {
						id: installingToastId,
					});
					handleLog("SW update found!", ...args);
					void wb.register();
				});
			});

			void navigator.serviceWorker.getRegistration().then((navSW) => {
				if (!navSW) {
					toast.loading("Installing updates...", {
						id: installingToastId,
					});
				}
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};
